/**
 * The palette's query understanding, independent of the component: prefix
 * modes, keyword scopes, command ranking and key combinations.
 */
import type {
  NxCommand,
  NxCommandCategory,
  NxCommandContext,
  NxCommandScope,
  NxCommandSource,
} from './commands';
import { fold, matchFields, tokens, type MatchRange } from './match';

/** A prefix mode, chosen by the first character of the query. */
export type NxPaletteMode = 'commands' | 'records' | 'people' | 'settings' | 'help' | 'calc';

export const MODE_PREFIXES: Record<string, NxPaletteMode> = {
  '>': 'commands',
  '#': 'records',
  '@': 'people',
  '/': 'settings',
  '?': 'help',
  '=': 'calc',
};

/** Splits a leading mode prefix off the query. */
export function parseMode(raw: string): { mode: NxPaletteMode | null; rest: string } {
  const mode = MODE_PREFIXES[raw.charAt(0)];
  return mode ? { mode, rest: raw.slice(1).trimStart() } : { mode: null, rest: raw };
}

/** Which categories of static commands a mode shows (null: all). */
export function commandCategories(mode: NxPaletteMode | null): NxCommandCategory[] | null {
  switch (mode) {
    case 'commands':
      return ['navigation', 'action', 'help'];
    case 'settings':
      return ['setting'];
    case 'records':
    case 'people':
    case 'calc':
      return [];
    default:
      return null;
  }
}

/** Whether a mode asks the search sources, and which category of them. */
export function sourceCategory(mode: NxPaletteMode | null): NxCommandCategory | 'all' | null {
  switch (mode) {
    case null:
      return 'all';
    case 'records':
      return 'record';
    case 'people':
      return 'person';
    default:
      return null;
  }
}

export interface ScopedQuery {
  source: NxCommandSource;
  scope: NxCommandScope;
  /** The query after the keyword. */
  rest: string;
}

/**
 * Recognises a leading scope keyword („angebot 3333“ → scope „Angebote“,
 * rest „3333“). The keyword has to be followed by a space — while the user is
 * still typing it, nothing is narrowed.
 */
export function parseScope(raw: string, sources: NxCommandSource[]): ScopedQuery | null {
  const m = /^(\S+)\s+(.*)$/.exec(raw);
  if (!m) return null;
  const word = fold(m[1]).text;
  for (const source of sources) {
    for (const scope of source.scopes ?? []) {
      if (scope.keywords.some((k) => fold(k).text === word)) {
        return { source, scope, rest: m[2] };
      }
    }
  }
  return null;
}

/**
 * The keyword a Tab completes a partly typed first word to, or null: the
 * shortest keyword of any scope that starts with the typed word.
 */
export function completeKeyword(raw: string, sources: NxCommandSource[]): string | null {
  if (!raw || /\s/.test(raw)) return null;
  const word = fold(raw).text;
  let best: string | null = null;
  for (const source of sources) {
    for (const scope of source.scopes ?? []) {
      for (const k of scope.keywords) {
        const f = fold(k).text;
        if (f.startsWith(word) && (best === null || f.length < fold(best).text.length)) best = k;
      }
    }
  }
  return best;
}

export interface RankedCommand {
  cmd: NxCommand;
  score: number;
  ranges: MatchRange[];
}

/**
 * Filters and ranks static commands: every query token must match the label,
 * keywords, hint or group. Commands of the open app and recently used ones
 * get a boost; without a query the registration order is kept.
 */
export function rankCommands(
  commands: NxCommand[],
  query: string,
  options: {
    categories?: NxCommandCategory[] | null;
    context?: NxCommandContext;
    recentIds?: string[];
  } = {}
): RankedCommand[] {
  const queryTokens = tokens(query);
  const categories = options.categories;
  const recent = options.recentIds ?? [];
  const out: RankedCommand[] = [];
  commands.forEach((cmd, order) => {
    if (categories && !categories.includes(cmd.category ?? 'action')) return;
    if (cmd.when && !safeWhen(cmd)) return;
    const m = matchFields(
      [
        { text: cmd.label, weight: 1 },
        { text: cmd.keywords?.join(' '), weight: 0.85 },
        { text: cmd.hint, weight: 0.6 },
        { text: cmd.group, weight: 0.5 },
      ],
      queryTokens
    );
    if (!m) return;
    let score = m.score;
    if (options.context?.app && cmd.app === options.context.app) score += 12;
    const r = recent.indexOf(cmd.id);
    if (r >= 0) score += Math.max(2, 10 - r);
    // Registration order breaks ties.
    score -= order * 1e-6;
    out.push({ cmd, score, ranges: m.ranges });
  });
  if (queryTokens.length) out.sort((a, b) => b.score - a.score);
  return out;
}

function safeWhen(cmd: NxCommand): boolean {
  try {
    return cmd.when!();
  } catch {
    return false;
  }
}

// ── Key combinations ──────────────────────────────────────────────────────

export interface KeyCombo {
  mod: boolean;
  shift: boolean;
  alt: boolean;
  key: string;
}

/** Parses "mod+shift+l" / "alt+1" / "g". Returns null for an empty spec. */
export function parseCombo(spec: string): KeyCombo | null {
  const parts = spec
    .toLowerCase()
    .split('+')
    .map((p) => p.trim())
    .filter(Boolean);
  const key = parts.pop();
  if (!key) return null;
  return {
    mod: parts.includes('mod') || parts.includes('ctrl') || parts.includes('cmd'),
    shift: parts.includes('shift'),
    alt: parts.includes('alt'),
    key,
  };
}

export function comboMatches(combo: KeyCombo, e: KeyboardEvent): boolean {
  return (
    e.key.toLowerCase() === combo.key &&
    (e.metaKey || e.ctrlKey) === combo.mod &&
    e.shiftKey === combo.shift &&
    e.altKey === combo.alt
  );
}

/** Names of the modifier keys outside macOS, in the UI language. */
export interface ModifierNames {
  mod: string;
  shift: string;
  alt: string;
}

/** A display form of a combo spec: "mod+shift+l" → "⌘⇧L" (macOS) / "Ctrl+Shift+L". */
export function formatCombo(
  spec: string,
  mac = isMac(),
  names: ModifierNames = { mod: 'Ctrl', shift: 'Shift', alt: 'Alt' }
): string {
  const c = parseCombo(spec);
  if (!c) return '';
  const named: Record<string, string> = { enter: '↵', escape: 'Esc', arrowup: '↑', arrowdown: '↓' };
  const key = named[c.key] ?? (c.key.length === 1 ? c.key.toUpperCase() : c.key);
  if (mac) return `${c.mod ? '⌘' : ''}${c.alt ? '⌥' : ''}${c.shift ? '⇧' : ''}${key}`;
  return [c.mod && names.mod, c.alt && names.alt, c.shift && names.shift, key]
    .filter(Boolean)
    .join('+');
}

export function isMac(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
}

/** A combo spec normalised for conflict checks. */
export function comboKey(spec: string): string {
  const c = parseCombo(spec);
  return c ? `${c.mod ? 'mod+' : ''}${c.alt ? 'alt+' : ''}${c.shift ? 'shift+' : ''}${c.key}` : '';
}

/** Whether a key event targets an editable element (then single keys must not fire). */
export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;
  return !!el.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]');
}
